<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import HighlightedProjects from "@components/HighlightedProjects.svelte";
	import Section from "@components/Section.svelte";
	import ServicesCards from "@components/ServicesCards.svelte";
	import TechnologyCards from "@components/TechnologyCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getDigitalizationAndAutomationPath } from "@core/schema/paths/getDigitalizationAndAutomationPath.js";
	import { getHireItTeamPath } from "@core/schema/paths/getHireItTeamPath.js";
	import { getKiosksPath } from "@core/schema/paths/getKiosksPath.js";
	import { getProjectRescuePath } from "@core/schema/paths/getProjectRescuePath.js";
	import { getWebAppsPath } from "@core/schema/paths/getWebAppsPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { fastestYearsInBusiness } from "../data/fastestFounded.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsIndustries } from "../data/getFaqsIndustries.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getServices } from "../data/getServices.js";
	import { getTechnologies } from "../data/getTechnologies.js";
	import type { IndustriesProps } from "./IndustriesProps.js";

	const { tenant } = getGlobalTemplateProps();
	const _: IndustriesProps = $props();
</script>

<Layout>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Baví nás cg[[zakázky]] pro e-commerce, FinTech cg[[nebo]] logistiku" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted text={`Za více než ${fastestYearsInBusiness} let na trhu jsme pomohli firmám z různých odvětví`} />
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Chci nezávaznou konzultaci" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="Spoléhají na nás cg[[podniky]] cg[[od]] e-shopů cg[[po]] automotive" />
		{/snippet}
	</Section>
	<ServicesCards cards={getServices(tenant)} />

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Ve kterých IT oblastech vám cg[[vytrhneme]] trn z paty?" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Ať už jste z jakéhokoliv odvětví,]] ve vývojářském studiu Fastest Solution vám kromě vývoje [{getWebAppsPath(
						tenant,
					)}][[webových]] či mobilních aplikací na zakázku podáme pomocnou ruku také se [{getProjectRescuePath(
						tenant,
					)}][[záchranou projektu]], [{getHireItTeamPath(
						tenant,
					)}][[rozšířením týmu]] nebo s [{getDigitalizationAndAutomationPath(
						tenant,
					)}][[digitalizací]] a [{getDigitalizationAndAutomationPath(
						tenant,
					)}][[automatizací]] firemních procesů. Dlouhodobě se specializujeme i na [{getKiosksPath(
						tenant,
					)}][[kiosková řešení]]."
				/>
			</p>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
		>
			<Formatted text="Nezávazně poptat" />
			<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-8" noPadding="bot">
		{#snippet headline()}
			<Formatted text="cg[[Pokladní systém]] pro Volvo a cg[[web]] o moderním skladování" />
		{/snippet}
	</Section>
	<HighlightedProjects />

	<Section background={Background.LightGradient} centered gridHeadline="container-8" noPadding="bot">
		{#snippet headline()}
			<Formatted text="Technologie cg[[přizpůsobujeme]] potřebám projektu" />
		{/snippet}
	</Section>
	<TechnologyCards technologies={getTechnologies(tenant)} />
	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Chci fastest služby
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Časté otázky k cg[[pronájmu IT týmu]]" />
		{/snippet}
		<FaqItems faqs={getFaqsIndustries(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
